/**
 * Sync Engine — processes queued API calls and uploads when online.
 *
 * - Listens for `online` event to trigger sync
 * - Periodic check every 30s when online
 * - Processes sync queue items FIFO, one at a time
 * - Retries failed items up to maxRetries (3), then removes them
 * - Uploads pending photos
 * - Pushes unsynced drafts to server
 * - Exposes a singleton instance
 */

import { getAccessToken } from '@/lib/api';
import {
  getNextSyncItem,
  removeSyncItem,
  incrementRetries,
  getNextPendingPhoto,
  removePendingPhoto,
  getAllDrafts,
  saveDraft,
  deleteDraft,
  getSyncQueueCount,
  getPendingPhotoCount,
  type SyncQueueEntry,
} from '@/lib/offline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

type StatusListener = (status: SyncStatus) => void;
type CountListener = (count: number) => void;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_URL = '/api';
const PERIODIC_INTERVAL_MS = 30_000; // 30 seconds
const MAX_RETRIES_DEFAULT = 3;

// ---------------------------------------------------------------------------
// SyncEngine class
// ---------------------------------------------------------------------------

class SyncEngine {
  private _status: SyncStatus = 'idle';
  private _isProcessing = false;
  private _statusListeners = new Set<StatusListener>();
  private _countListeners = new Set<CountListener>();
  private _periodicTimer: ReturnType<typeof setInterval> | null = null;
  private _started = false;
  private _lastSyncAt: Date | null = null;
  private _pendingCount = 0;

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /**
   * Start listening for online events and begin periodic sync.
   */
  start(): void {
    if (this._started) return;
    this._started = true;

    // Set initial status
    this._setStatus(navigator.onLine ? 'idle' : 'offline');

    // Listen for connectivity changes
    window.addEventListener('online', this._onOnline);
    window.addEventListener('offline', this._onOffline);

    // Start periodic sync when online
    this._periodicTimer = setInterval(() => {
      if (navigator.onLine && !this._isProcessing) {
        void this.processAll();
      }
    }, PERIODIC_INTERVAL_MS);

    // Initial sync if online
    if (navigator.onLine) {
      void this.processAll();
    }
  }

  /**
   * Stop all listeners and timers. Idempotent.
   */
  stop(): void {
    if (!this._started) return;
    this._started = false;

    window.removeEventListener('online', this._onOnline);
    window.removeEventListener('offline', this._onOffline);

    if (this._periodicTimer !== null) {
      clearInterval(this._periodicTimer);
      this._periodicTimer = null;
    }
  }

  // -----------------------------------------------------------------------
  // Event handlers (bound arrows for stable references)
  // -----------------------------------------------------------------------

  private _onOnline = (): void => {
    this._setStatus('idle');
    void this.processAll();
  };

  private _onOffline = (): void => {
    this._setStatus('offline');
  };

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Get current sync status.
   */
  getStatus(): SyncStatus {
    return this._status;
  }

  /**
   * Get timestamp of last successful sync.
   */
  getLastSyncAt(): Date | null {
    return this._lastSyncAt;
  }

  /**
   * Get the current count of pending items.
   */
  getPendingCount(): number {
    return this._pendingCount;
  }

  /**
   * Subscribe to status changes. Returns unsubscribe function.
   */
  onStatusChange(listener: StatusListener): () => void {
    this._statusListeners.add(listener);
    return () => {
      this._statusListeners.delete(listener);
    };
  }

  /**
   * Subscribe to pending count changes. Returns unsubscribe function.
   */
  onCountChange(listener: CountListener): () => void {
    this._countListeners.add(listener);
    return () => {
      this._countListeners.delete(listener);
    };
  }

  /**
   * Force a full sync cycle (queue + photos + drafts).
   * Returns once all processing is done.
   */
  async processAll(): Promise<void> {
    if (this._isProcessing) return;
    if (!navigator.onLine) {
      this._setStatus('offline');
      return;
    }

    this._isProcessing = true;
    this._setStatus('syncing');

    try {
      await this._processQueue();
      await this._processPhotos();
      await this._syncDrafts();
      await this._refreshPendingCount();

      this._lastSyncAt = new Date();
    } catch (err) {
      // Log per diagnostica futura; lo status NON va a 'error' perche' quel
      // banner si rivelava sempre rumore non azionabile (IDB race al boot,
      // 401 pre-login, ecc). Gli item che falliscono davvero vengono retried
      // o rimossi dopo maxRetries da _processQueue stesso.
      console.warn('[sync] processAll failed:', err);
    } finally {
      this._setStatus('idle');
      this._isProcessing = false;
    }
  }

  // -----------------------------------------------------------------------
  // Queue processing
  // -----------------------------------------------------------------------

  /**
   * Process all items in the sync queue, one by one (FIFO).
   */
  private async _processQueue(): Promise<void> {
    let item = await getNextSyncItem();

    while (item !== null) {
      if (!navigator.onLine) return; // bail if we went offline

      const success = await this._executeSyncItem(item);

      if (success) {
        await removeSyncItem(item.id!);
      } else {
        // Increment retries or remove if exceeded
        if (item.retries >= (item.maxRetries || MAX_RETRIES_DEFAULT) - 1) {
          // Max retries exceeded — remove to prevent blocking the queue
          await removeSyncItem(item.id!);
        } else {
          await incrementRetries(item.id!);
          // Skip to next item to avoid infinite loop on persistent failures
        }
      }

      // Get next item
      const next = await getNextSyncItem();
      // If the same item comes back (failed but not removed), break to avoid loops
      if (next && next.id === item.id) break;
      item = next;
    }
  }

  /**
   * Execute a single sync queue item via fetch.
   * Returns true if the request succeeded (2xx), false otherwise.
   */
  private async _executeSyncItem(item: SyncQueueEntry): Promise<boolean> {
    try {
      const headers: Record<string, string> = {
        ...(item.headers ?? {}),
      };

      // Inject current access token
      const token = getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Set Content-Type for JSON bodies
      if (item.body !== undefined && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      const res = await fetch(`${BASE_URL}${item.url}`, {
        method: item.method,
        headers,
        body: item.body !== undefined ? JSON.stringify(item.body) : undefined,
      });

      // Consider 2xx and 409 (conflict / already exists) as success
      return res.ok || res.status === 409;
    } catch {
      // Network error — not online or server unreachable
      return false;
    }
  }

  // -----------------------------------------------------------------------
  // Photo upload
  // -----------------------------------------------------------------------

  /**
   * Upload pending photos one by one.
   */
  private async _processPhotos(): Promise<void> {
    let photo = await getNextPendingPhoto();

    while (photo !== null) {
      if (!navigator.onLine) return;

      try {
        const token = getAccessToken();
        const formData = new FormData();
        formData.append('file', photo.blob, `photo_${photo.id}.webp`);
        formData.append('sessionId', String(photo.sessionId));
        formData.append('clientId', String(photo.clientId));
        formData.append('photoType', photo.photoType);

        const res = await fetch(
          `${BASE_URL}/photos/upload`,
          {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          },
        );

        if (res.ok || res.status === 409) {
          await removePendingPhoto(photo.id!);
        } else {
          // Skip this photo for now, will retry next cycle
          break;
        }
      } catch {
        // Network error — stop processing photos
        break;
      }

      photo = await getNextPendingPhoto();
    }
  }

  // -----------------------------------------------------------------------
  // Draft sync
  // -----------------------------------------------------------------------

  /**
   * Push all unsynced drafts to the server.
   */
  private async _syncDrafts(): Promise<void> {
    const drafts = await getAllDrafts();

    for (const draft of drafts) {
      if (!navigator.onLine) return;

      try {
        const token = getAccessToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(
          `${BASE_URL}/checkups/${draft.sessionId}`,
          {
            method: 'PATCH',
            headers,
            body: JSON.stringify({
              answers: draft.answers,
              measurements: draft.measurements,
            }),
          },
        );

        if (res.ok) {
          // Mark as synced (keep in IDB for recovery, but mark done)
          await saveDraft({ ...draft, synced: true });
        } else if (res.status === 404) {
          // Session no longer exists server-side — remove local draft
          await deleteDraft(draft.sessionId);
        }
        // Other errors: leave unsynced, will retry next cycle
      } catch {
        // Network error — stop processing drafts
        break;
      }
    }
  }

  // -----------------------------------------------------------------------
  // Internal helpers
  // -----------------------------------------------------------------------

  private _setStatus(status: SyncStatus): void {
    if (this._status === status) return;
    this._status = status;
    for (const listener of this._statusListeners) {
      try {
        listener(status);
      } catch {
        // Don't let a bad listener break the engine
      }
    }
  }

  private async _refreshPendingCount(): Promise<void> {
    try {
      const [syncCount, photoCount] = await Promise.all([
        getSyncQueueCount(),
        getPendingPhotoCount(),
      ]);
      const drafts = await getAllDrafts();
      const count = syncCount + photoCount + drafts.length;
      this._pendingCount = count;
      for (const listener of this._countListeners) {
        try {
          listener(count);
        } catch {
          // Don't let a bad listener break the engine
        }
      }
    } catch {
      // IndexedDB error — not critical
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const syncEngine = new SyncEngine();
